import { useTranslation } from "react-i18next";
import { useAuth } from "@/providers/auth-provider";
import { useUserProfile } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "@tanstack/react-router";

/**
 * Get user initials from name
 */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function UserMenu() {
  const { user, handleSignOut, isLoading } = useAuth();
  const { profile } = useUserProfile();
  const { t } = useTranslation("common");

  if (!user) {
    return null;
  }

  const profileName = profile ? `${profile.firstName} ${profile.lastName}`.trim() : "";
  const displayName = profileName || user.name || user.email;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={isLoading}
          className="h-9 gap-2 rounded-full px-1.5 lg:px-2"
          aria-label={t("auth.openUserMenu")}
        >
          {user.image ? (
            <img
              src={user.image}
              alt={displayName}
              className="h-8 w-8 rounded-full ring-2 ring-border"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground ring-2 ring-border">
              {getInitials(displayName)}
            </div>
          )}
          <span className="hidden lg:block text-sm font-medium text-foreground">
            {displayName}
          </span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-44 p-1">
        <Button asChild variant="ghost" className="w-full justify-start">
          <Link to="/settings">{t("nav.settings")}</Link>
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void handleSignOut();
          }}
          disabled={isLoading}
          className="w-full justify-start"
        >
          {t("auth.logOut")}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
